/* eslint-disable @typescript-eslint/member-ordering */
import {makeObservable, computed, observable, action} from "mobx";

import {FieldAggregationConfiguration} from "../config/stations-configuration";
import {Kiwis} from "./kiwis";
import {Measurement} from "./measurement";
import dayjs = require("dayjs");
import {FieldOverrideConfiguration} from "../config/field-override-configuration";
import {KiwisConfiguration} from "../config/kiwis-configuration";

dayjs.locale("de-ch");

export interface StationProps {
    baseId?: number;
    oid?: number;
    no?: number;
    id?: string;
    uuid?: string;
    name?: string;
    catchmentNo?: number;
    catchmentId?: string;
    catchmentName?: string;
    catchmentSizeFormal?: string | number;
    catchmentSize?: string | number;

    dataOwner?: string;
    description?: string;
    x?: number;
    y?: number;
    siteNo?: number;
    siteId?: string;
    siteUuid?: string;
    siteName?: string;
    parameterTypeNames?: string;
    stationParameterName?: string;
    objectType?: string;
    specialism?: string;
    locationType?: string;
    topic?: string;
    objectTypeShortName?: string;
    longName?: string;
    elevation?: string | number;
    online?: string;

    suitabilityZone?: string;
    soilType?: string;
    soilDescription?: string;

    diaryStatus?: string;
    status?: string;
    store?: Kiwis;

    HQ2?: string;
    HQ5?: string;
    HQ10?: string;
    HQ20?: string;
    HQ30?: string;
    HQ50?: string;
    HQ100?: string;
    HQ300?: string;
    HQxValidFrom?: string;
    HQxValidTill?: string;

    Q347?: string;
    Q182?: string;
    Q182Q347ValidFrom?: string;
    Q182Q347ValidTill?: string;

    QGS2?: string;
    QGS3?: string;
    QGS4?: string;
    QGS5?: string;
    QGSxValidFrom?: string;
    QGSxValidTill?: string;

    WLGS2?: string;
    WLGS3?: string;
    WLGS4?: string;
    WLGS5?: string;
    WLGSxValidFrom?: string;
    WLGSxValidTill?: string;

    longTermValMin?: string;
    longTermValMean?: string;
    longTermValMax?: string;
    longTermValValidFrom?: string;
    longTermValMinStartDate?: string;
    longTermValMinEndDate?: string;
}

// noinspection JSUnusedGlobalSymbols
export class Station {
    @observable oid: number; // internal unique identifier
    @observable baseId: number; // Id specific for object type e.g. ("Fliessgewässer - Hydrometrie": 110)
    @observable no: string; // station number
    @observable id: number; // station ID
    @observable uuid: string; // Unique database independent ID
    @observable name: string; // station name

    /**
     * Fachbereich
     */
    objectType: string;
    /**
     * Fachbereich of (first) related station object
     * Important for stations that have multiple object types
     */
    @observable relatedObjectType: string;

    /**
     * Related station objects
     * Station objects with same id, but e.g. different object type
     */
    relatedStations = observable.map<string, Station>();

    /**
     * Sub grouping object type
     * e.g. Boden, Grundwasser, Oberflächengewässer
     */
    specialism: string;

    /**
     * Messstellentyp
     */
    locationType: string;

    catchmentNo: number; // catchment number
    catchmentId: string; // catchment ID
    catchmentName: string; // catchment name
    /**
     * Formale Grösse des Einzugsgebiets
     * gem. EZGG-CH
     */
    catchmentSizeFormal: number;
    /**
     * Fläche Stationseinzugsgebiet
     * Hydrologisch für die Station unmittelbar relevante Einzugsgebietsfläche
     */
    catchmentSize: string; // catchment size
    /**
     * Owner of the data (Datenherr)
     */
    dataOwner: string;
    /**
     * Description of station
     * (unstructured free text in WISKI without domain)
     */
    description: string;

    @observable x: number;
    @observable y: number;

    /**
     * If station measurement data is online connected
     */
    online: string;

    siteNo: number;
    siteId: string;
    siteUuid: string;	// Unique database independent ID
    siteName: string;

    /**
     * External website URL
     */
    website: string;

    /**
     * Parameter types from WISKI are concatenated.
     * In WISKI result, stations are contained multiple times, once for each paramaterTypeName.
     */
    parameterTypeNames: string;

    /**
     * Further specification of parameter type
     * e.g. Bodensaugspannung 2 [35cm]
     */
    stationParameterName: string;

    /**
     * Short name for object type
     */
    objectTypeShortName: string;

    /**
     * The topic of the station. This is not available in WISKI and derived from configuration.
     * (See station.objectTypes.topics)
     */
    topic: string;

    /**
     * Long name for station
     */
    longName: string;

    /**
     * Elevation above sea level
     */
    elevation: number;

    /**
     * Klimaeignungszone (Boden)
     */
    suitabilityZone: string;
    /**
     * Bodentyp
     */
    soilType: string;
    /**
     * Bodenart (Oberboden, Unterboden)
     */
    soilDescription: string;

    /**
     * Status of station
     * 'Aktiv', 'Geschlossen', ''
     */
    @observable status: string;

    /**
     * Level of administrative unit
     * e.g. Bund, Kanton, Gemeinde
     */
    adminLevel: string;

    /**
     * Name of administrative level
     * e.g. Luzern, Bern, Obwalden, Aargau
     */
    adminName: string;

    /**
     * Status and date of last status change
     * e.g. 2015-11-26 00:00:00: Aktiv
     */
    diaryStatus: string;

    /**
     * Code based on measurements, calculated by measurementCodeExpression
     */
    @observable measurementCode: string;

    // --------------------------------------------------------------------------------------------
    /**
     * Station gauge origin (Pegelnullpunkt)
     */
    gaugeOrigin: string;

    /**
     * Threshold values for runoff
     */
    HQ2: string;
    HQ5: string;
    HQ10: string;
    HQ20: string;
    HQ30: string;
    HQ50: string;
    HQ100: string;
    HQ300: string;
    HQxValidFrom: string;
    HQxValidTill: string;

    // --------------------------------------------------------------------------------------------
    /**
     * Thresholds for water level
     */
    /**
     * low water
     */
    Q347: string;
    /**
     * Statistical water level that is reached or overrun at at least 182 days per year
     */
    Q182: string;
    Q182Q347ValidFrom: string;
    Q182Q347ValidTill: string;

    // --------------------------------------------------------------------------------------------
    /**
     * Thresholds for accident levels (runoff)
     */
    QGS2: string;
    QGS3: string;
    QGS4: string;
    QGS5: string;
    QGSxValidFrom: string;
    QGSxValidTill: string;
    /**
     * Thresholds for accident levels (gauge, water level)
     */
    WLGS2: string;
    WLGS3: string;
    WLGS4: string;
    WLGS5: string;
    WLGSxValidFrom: string;
    WLGSxValidTill: string;
    // --------------------------------------------------------------------------------------------

    /**
     * Thresholds for soil
     * > 20 cbar: dry
     * 10 - 20 cbar: damp
     * 6 - 10 cbar: very damp
     * < 6 cbar: wet
     */
    soilDry = 90;
    soilDamp = 20;
    soilVeryDamp = 10;
    soilWet = 6;
    // --------------------------------------------------------------------------------------------

    /**
     * Thresholds for MSK classification
     * 1: sehr gut
     * 2: gut
     * 3: mässig
     * 4: unbefriedigend
     * 5: schlecht
     */
    msk1 = 1;
    msk2 = 2;
    msk3 = 3;
    msk4 = 4;
    msk5 = 5;
    // --------------------------------------------------------------------------------------------

    /**
     * Thresholds for MSK classification
     * 1: ausgezeichnet (A)
     * 2: gut (B)
     * 3: ausreichend (C)
     * 4: mangelhaft (D)
     */
    eua1 = 1;
    eua2 = 2;
    eua3 = 3;
    eua4 = 4;
    // --------------------------------------------------------------------------------------------

    /**
     * Thresholds for Nitrat classification
     * 0: kein Messwert
     * 1: <= 25mg/l
     * 2: > 25 mg/l
     */
    nitrat0 = null;
    nitrat1 = 25;
    nitrat2 = 9000;
    // --------------------------------------------------------------------------------------------

    /**
     * Thresholds groundwater level
     * Values are taken from time series (10-Year.Min, 10-Year.Mean, 10-Year.Max)
     */
    @observable longTermValMin: string;
    @observable longTermValMean: string;
    @observable longTermValMax: string;
    /**
     * Threshold 10-year period is calculated by date of the last value in threshold time series
     */
    @observable longTermValMinStartDate: string;
    @observable longTermValMinEndDate: string;
    @observable longTermValMeanStartDate: string;
    @observable longTermValMeanEndDate: string;
    @observable longTermValMaxStartDate: string;
    @observable longTermValMaxEndDate: string;
    /**
     * Validity dates are taken from station params
     */
    @observable longTermValValidFrom: string;  // value
    @observable longTermValValidTill: string;

    /**
     * Current measurements of available time series
     * Measurement are stored by key available at config.stationParameterMeasurementFieldMappings
     */
    currentMeasurements = observable.map<string, Measurement>();

    store: Kiwis;

    constructor(props?: StationProps) {
        if (props) {
            props = this.formatElevation(props);
            props = this.formatCatchmentSize(props);
            Object.assign(this, props);
        }
        makeObservable(this); // make observable after setting properties to avoid 'strict' messages
    }

    @action
    calculateMeasurementCode(config: KiwisConfiguration) {
        if (this.objectType === "Boden") {
            this.measurementCode = this.calculateSoilMeasurementCode(config);
        }
        if (this.objectType === "Fliessgewässer - Qualität") {
            this.measurementCode = this.calculateMskCode();
        }
        if (this.objectType === "Badewasserqualität") {
            this.measurementCode = this.calculateEuaCode();
        }
        if (this.objectType === "Grundwasser - Qualität") {
            this.measurementCode = this.calculateNitratCode(config);
        }
    }

    /**
     * Convert elevation to a number
     *
     * @param props
     */
    formatElevation(props: StationProps) {
        if (props.elevation) {
            const elev = Number(props.elevation);
            props.elevation = isNaN(elev) ? -1 : elev;
        }
        return props;
    }

    /**
     * Fix catchment size formats
     *
     * @param props Station properties
     */
    formatCatchmentSize(props: StationProps) {
        if (props.catchmentSizeFormal) {
            const sizeFormal = Number(props.catchmentSizeFormal);
            props.catchmentSizeFormal = isNaN(sizeFormal) ? -1 : sizeFormal;
        }
        if (props.catchmentSize) {
            const sizeStr = String(props.catchmentSize)
                .replace("BIG_AREA", "")
                .replace("km²", "")
                .replace(" ", "");
            const size = Number(sizeStr);
            props.catchmentSize = isNaN(size) ? -1 : size;
        }
        return props;
    }

    @computed
    get coordinates() {
        return `${this.x.toFixed()} / ${this.y.toFixed()}`;
    }

    get diaryStatusFormated() {
        const [date, status] = this.diaryStatus.split(": ");
        return `${dayjs(date).format("DD.MM.YYYY")} | ${status}`;
    }

    /**
     * Apply overrides
     * Overrides can be used to change original values
     */
    applyOverrides(overrides: FieldOverrideConfiguration[]) {
        if (!overrides || !overrides.length) {
            return;
        }

        const applicableOverrides = overrides.filter((ov) =>
            ov.baseIds.indexOf(this.baseId) !== -1
        );

        // noinspection DuplicatedCode
        const that = this;
        applicableOverrides.forEach((over: FieldOverrideConfiguration) => {
            if (over.queryExpression.evaluate(that)) {
                let value = over.valueExpression;
                if (value.includes("${")) {
                    value = `${value.evaluate(that)}`;
                }
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                that[over.fieldName] = value;
            }
        });
    }

    /**
     * Merge properties of source to this station
     * KIWIS is returning flat JSON only. If parameter type is requested, stations are returned multiple times.
     * object_type is also duplicated when parametertype_name is requested (e.G.: Allgemein;Niederschlag is resulting
     * in two objects Allgemein and Niederschlag
     */
    aggregate(source: StationProps, fieldAggregations: FieldAggregationConfiguration[]) {
        source = this.formatElevation(source);
        source = this.formatCatchmentSize(source);

        fieldAggregations.forEach((aggr) => {
            source = this.aggregateProperties(source, aggr);
        });

        Object.assign(this, source);
    }

    /**
     * Merge values of properties
     *
     * @param props
     * @param config
     */
    aggregateProperties(props: StationProps, config: FieldAggregationConfiguration) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const value = props[config.fieldName];
        if (!value) {
            // nothing to aggregate
            return props;
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const current_values = this[config.fieldName];
        const isContained = current_values.indexOf(value) !== -1;
        if (isContained) {
            // already contained --> keep property
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            props[config.fieldName] = current_values;
        } else {
            // not contained --> merge
            // split previously merged values, so these are also sorted
            const values_list = current_values.split(config.separator);
            const values = [...values_list, value];
            if (config.sort) {
                values.sort((n1, n2) => {
                    if (n1 > n2) {
                        return 1;
                    }

                    if (n1 < n2) {
                        return -1;
                    }

                    return 0;
                });
            }
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            props[config.fieldName] = values.join(config.separator);
        }
        return props;
    }

    /**
     * Calculate specific code for soil stations
     */
    private calculateSoilMeasurementCode(config: KiwisConfiguration) {
        const expression = config.stations.measurementCodeExpression[this.objectType];
        const stops = expression.stops;

        const rainfall = this.currentMeasurements.get("rainfall24");
        if (rainfall && rainfall.value > 10) {
            const rainCode = stops[0].code;
            return `${rainCode}${rainCode}`;
        } else {
            const top = this.currentMeasurements.get("soiltension20");
            const bottom = this.currentMeasurements.get("soiltension35");
            if (!top || !bottom) {
                return "0000";
            }
            const topCode = this.classifyMeasurementValue(top.value, stops);
            const bottomCode = this.classifyMeasurementValue(bottom.value, stops);
            return `${topCode}${bottomCode}`;
        }
    }

    /**
     * Get code value based on class stops
     *
     * @param value
     * @param stops
     */
    private classifyMeasurementValue(value: number | null | undefined, stops: any[]) {
        if (value == null) {
            const nullStop = stops.filter((s) => s.value == null);
            if (nullStop && nullStop.length > 0) {
                return nullStop[0].code;
            } else {
                throw new Error("No code defined for null values.");
            }
        }

        for (let i = 0; i < stops.length; i++) {
            const stop = stops[i];
            if (value < stop.value) {
                return stop.code;
            }
        }
        return null;
    }

    /**
     * Calculate specific code for MSK classified data
     */
    private calculateMskCode() {
        const fields = [
            "mskAmmoniumN", "mskBSB5", "mskDOC", "mskNitratN", "mskNitritN", "mskPhosphatP",
            "mskPhosphorG", "mskPhosphorT"
        ];
        // get all not null measurement values
        const values = fields.map((field: string) => {
            const measurement = this.currentMeasurements.get(field);
            return measurement ? measurement.value : 0;
        }) || [0];
        // use most 'bad' value
        return Math.max.apply(null, values) ? `MSK${Math.max.apply(null, values)}` : "MSK0";
    }

    /**
     * Calculate specific code for EUA classified data
     */
    private calculateEuaCode() {
        const fields = ["qualityBathEUA"];
        const values = fields.map((field: string) => {
            const measurement = this.currentMeasurements.get(field);
            return measurement ? measurement.value : 0;
        }) || [0];
        // use most 'bad' value
        return Math.max.apply(null, values) ? `EUA${Math.max.apply(null, values)}` : "EUA0";
    }

    /**
     * Calculate specific code for EUA classified data
     */
    private calculateNitratCode(config: KiwisConfiguration) {
        const expression = config.stations.measurementCodeExpression[this.objectType];
        const stops = expression.stops;

        const nitrat = this.currentMeasurements.get("nitrat");
        return this.classifyMeasurementValue(nitrat?.value, stops);
    }
}
